/**
 * Карточка клиента — полный профиль с заказами, бонусами, статистикой.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Button, Card, Col, Descriptions, Empty, Form, Input, InputNumber,
  message, Modal, Popconfirm, Row, Space, Statistic, Table, Tabs, Tag, Typography,
} from 'antd';
import {
  ArrowLeftOutlined, DeleteOutlined, EditOutlined, ExclamationCircleOutlined,
  GiftOutlined, MinusCircleOutlined, PhoneOutlined,
  PlusCircleOutlined, ShoppingCartOutlined, UserOutlined, MailOutlined,
  CalendarOutlined, WalletOutlined, TagsOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import ClientTagsBlock from '../components/ClientTagsBlock';
import ClientStatesTab from '../components/ClientStatesTab';
import ClientVariablesTab from '../components/ClientVariablesTab';
import { useAuthStore } from '../store/authStore';
import { useScenarioScope } from './marketing/useScenarioScope';

const { Title, Text } = Typography;

interface ProfileData {
  user: {
    id: number;
    phone: string | null;
    name: string | null;
    email: string | null;
    bonus_balance: number;
    auth_provider: string | null;
    is_blocked: boolean;
    created_at: string | null;
  };
  stats: {
    orders_count: number;
    total_spent: number;
    avg_check: number;
    first_order_at: string | null;
    last_order_at: string | null;
    promo_codes_used: number;
  };
  channels: Record<string, number>;
  orders: OrderData[];
  bonus_balance: number;
  bonus_breakdown: BonusBreakdown[];
  bonus_transactions: BonusTx[];
  promo_codes: PromoUsage[];
}

interface OrderData {
  id: number;
  order_number: string;
  status: string;
  delivery_method: string;
  payment_method: string;
  payment_status: string;
  subtotal: number;
  discount_amount: number;
  bonus_used: number;
  total: number;
  promo_code_text: string | null;
  channel: string;
  comment: string | null;
  delivery_address: string | null;
  created_at: string;
  items: OrderItemData[];
}

interface OrderItemData {
  product_name: string;
  variant_name: string | null;
  price: number;
  quantity: number;
  total: number;
  addons: { name: string; price: number; qty: number }[];
}

interface BonusBreakdown {
  reason: string;
  earned: number;
  spent: number;
  count: number;
}

interface BonusTx {
  id: number;
  amount: number;
  balance_after: number;
  reason: string;
  comment: string | null;
  order_id: number | null;
  created_at: string;
}

interface PromoUsage {
  code: string;
  times_used: number;
  total_discount: number;
}

const REASON_LABELS: Record<string, string> = {
  welcome: 'Приветственный бонус',
  earn: 'Кэшбэк за заказ',
  spend: 'Списание',
  expire: 'Сгорание',
  admin_adjust: 'Ручная корректировка',
  referral: 'Реферальная программа',
};

const REASON_COLORS: Record<string, string> = {
  welcome: 'purple',
  earn: 'green',
  spend: 'orange',
  expire: 'red',
  admin_adjust: 'blue',
  referral: 'cyan',
};

const STATUS_COLORS: Record<string, string> = {
  new: 'blue', confirmed: 'cyan', cooking: 'orange', ready: 'gold',
  delivering: 'geekblue', delivered: 'green', completed: 'green', cancelled: 'red',
};

const STATUS_LABELS: Record<string, string> = {
  new: 'Новый', confirmed: 'Подтверждён', cooking: 'Готовится', ready: 'Готов',
  delivering: 'Доставляется', delivered: 'Доставлен', completed: 'Завершён', cancelled: 'Отменён',
};

const CHANNEL_LABELS: Record<string, string> = {
  website: 'Сайт', app: 'Приложение', vk: 'ВКонтакте', telegram: 'Telegram',
};

const ClientProfilePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [adjustModal, setAdjustModal] = useState(false);
  const [adjustLoading, setAdjustLoading] = useState(false);
  const [form] = Form.useForm();
  const [editModal, setEditModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editForm] = Form.useForm();
  const canReadStates = useAuthStore((s) => !!s.admin && ['owner', 'admin', 'manager'].includes(s.admin.role));
  // Минимальная identity-ремонт обёртка только для новой вкладки «Переменные
  // и скидка» (без переписывания остальных секций страницы): useScenarioScope
  // сама по себе игнорирует устаревшие ответы через isCurrent(), но не чистит
  // уже отрисованное состояние — key={variablesIdentity} синхронно сбрасывает
  // всё поддерево вкладки (черновики, модалки, pending-ledger хуки) в том же
  // commit, где сменился tenant/admin/role/auth/switching, СИММЕТРИЧНО
  // ClientStateDefinitionsPage.tsx.
  const variablesScope = useScenarioScope();
  const variablesIdentity = useAuthStore((s) => [s.tenant?.id, s.admin?.id, s.admin?.role,
    s.isAuthenticated, s.isSwitching].join(':'));

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/clients/${id}/profile`);
      setData(res.data);
    } catch {
      message.error('Ошибка загрузки профиля');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const handleBonusAdjust = async (values: { amount: number; comment: string }) => {
    setAdjustLoading(true);
    try {
      await api.post(`/admin/clients/${id}/bonus-adjust`, null, {
        params: { amount: values.amount, comment: values.comment || '' },
      });
      message.success(values.amount > 0 ? 'Бонусы начислены' : 'Бонусы списаны');
      setAdjustModal(false);
      form.resetFields();
      fetchProfile();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      message.error(err.response?.data?.detail || 'Ошибка');
    } finally {
      setAdjustLoading(false);
    }
  };

  const handleEditClient = async (values: { name?: string; phone?: string }) => {
    setEditLoading(true);
    try {
      await api.put(`/admin/clients/${id}`, values);
      message.success('Клиент обновлён');
      setEditModal(false);
      fetchProfile();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      message.error(err.response?.data?.detail || 'Ошибка обновления');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteClient = async () => {
    try {
      await api.delete(`/admin/clients/${id}`);
      message.success('Клиент удалён');
      navigate('/clients');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      message.error(err.response?.data?.detail || 'Ошибка удаления');
    }
  };

  if (loading || !data) {
    return <div style={{ textAlign: 'center', padding: 48 }}><Text>Загрузка...</Text></div>;
  }

  const { user, stats, channels, orders, bonus_breakdown, bonus_transactions, promo_codes } = data;

  const orderColumns: ColumnsType<OrderData> = [
    {
      title: '№', dataIndex: 'order_number', width: 100,
      render: (v: string) => <Text strong>#{v}</Text>,
    },
    {
      title: 'Дата', dataIndex: 'created_at', width: 150,
      render: (d: string) => dayjs(d).format('DD.MM.YYYY HH:mm'),
      sorter: (a: OrderData, b: OrderData) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Статус', dataIndex: 'status', width: 120,
      render: (s: string) => <Tag color={STATUS_COLORS[s]}>{STATUS_LABELS[s] || s}</Tag>,
    },
    {
      title: 'Товары', dataIndex: 'items', ellipsis: true,
      render: (items: OrderItemData[]) =>
        items.map((i: OrderItemData) =>
          `${i.product_name}${i.variant_name ? ` (${i.variant_name})` : ''} x${i.quantity}`
        ).join(', '),
    },
    {
      title: 'Сумма', dataIndex: 'total', width: 100, align: 'right' as const,
      render: (v: number) => `${v} ₽`,
      sorter: (a: OrderData, b: OrderData) => a.total - b.total,
    },
    {
      title: 'Скидка', dataIndex: 'discount_amount', width: 80, align: 'right' as const,
      render: (v: number) => v > 0 ? <Text type="success">-{v} ₽</Text> : '—',
    },
    {
      title: 'Бонусы', dataIndex: 'bonus_used', width: 80, align: 'right' as const,
      render: (v: number) => v > 0 ? <Text type="warning">-{v}</Text> : '—',
    },
    {
      title: 'Промокод', dataIndex: 'promo_code_text', width: 110,
      render: (v: string | null) => v ? <Tag color="blue">{v}</Tag> : '—',
    },
    {
      title: 'Канал', dataIndex: 'channel', width: 100,
      render: (c: string) => <Tag>{CHANNEL_LABELS[c] || c}</Tag>,
    },
  ];

  const bonusColumns: ColumnsType<BonusTx> = [
    {
      title: 'Дата', dataIndex: 'created_at', width: 150,
      render: (d: string) => dayjs(d).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: 'Тип', dataIndex: 'reason', width: 200,
      render: (r: string) => <Tag color={REASON_COLORS[r] || 'default'}>{REASON_LABELS[r] || r}</Tag>,
    },
    {
      title: 'Сумма', dataIndex: 'amount', width: 100, align: 'right' as const,
      render: (v: number) => (
        <Text type={v > 0 ? 'success' : 'danger'} strong>
          {v > 0 ? '+' : ''}{v}
        </Text>
      ),
    },
    {
      title: 'Баланс после', dataIndex: 'balance_after', width: 110, align: 'right' as const,
    },
    {
      title: 'Заказ', dataIndex: 'order_id', width: 80,
      render: (v: number | null) => v ? `#${v}` : '—',
    },
    {
      title: 'Комментарий', dataIndex: 'comment', ellipsis: true,
      render: (v: string | null) => v || '—',
    },
  ];

  const totalVisits = Object.values(channels).reduce((s, v) => s + v, 0);

  return (
    <div>
      <Space style={{ marginBottom: 24 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/clients')}>Назад</Button>
        <Title level={3} style={{ margin: 0 }}>
          <UserOutlined style={{ marginRight: 8 }} />
          {user.name || 'Без имени'} — {user.phone || 'Нет телефона'}
        </Title>
        {user.is_blocked && <Tag color="red">Заблокирован</Tag>}
        <Button
          icon={<EditOutlined />}
          onClick={() => {
            editForm.setFieldsValue({ name: user.name || '', phone: user.phone || '' });
            setEditModal(true);
          }}
        >
          Редактировать
        </Button>
        <Popconfirm
          title="Удаление клиента"
          description="Вы уверены? Это действие необратимо."
          onConfirm={handleDeleteClient}
          okText="Удалить"
          cancelText="Отмена"
          okButtonProps={{ danger: true }}
          icon={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
        >
          <Button danger icon={<DeleteOutlined />}>Удалить</Button>
        </Popconfirm>
      </Space>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="Заказов" value={stats.orders_count} prefix={<ShoppingCartOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="Потрачено" value={stats.total_spent} suffix="₽" precision={0} prefix={<WalletOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="Средний чек" value={stats.avg_check} suffix="₽" precision={0} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Бонусный баланс"
              value={user.bonus_balance}
              prefix={<GiftOutlined />}
              valueStyle={{ color: '#FF6B35' }}
            />
          </Card>
        </Col>
      </Row>

      <Card size="small" style={{ marginBottom: 24 }}>
        <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small">
          <Descriptions.Item label={<><PhoneOutlined /> Телефон</>}>{user.phone || '—'}</Descriptions.Item>
          <Descriptions.Item label={<><MailOutlined /> Email</>}>{user.email || '—'}</Descriptions.Item>
          <Descriptions.Item label={<><CalendarOutlined /> Регистрация</>}>
            {user.created_at ? dayjs(user.created_at).format('DD.MM.YYYY HH:mm') : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Авторизация">
            <Tag>
              {user.auth_provider === 'phone' ? 'Телефон'
                : user.auth_provider === 'vk' ? 'ВКонтакте'
                : user.auth_provider === 'telegram' ? 'Telegram'
                : user.auth_provider || '—'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Первый заказ">
            {stats.first_order_at ? dayjs(stats.first_order_at).format('DD.MM.YYYY') : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Последний заказ">
            {stats.last_order_at ? dayjs(stats.last_order_at).format('DD.MM.YYYY') : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Каналы заказов">
            {totalVisits > 0
              ? Object.entries(channels).map(([ch, cnt]) => (
                  <Tag key={ch}>{CHANNEL_LABELS[ch] || ch}: {cnt}</Tag>
                ))
              : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Промокоды использованы">{stats.promo_codes_used}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Tabs defaultActiveKey="orders" items={[
        ...(variablesScope.enabled ? [{
          key: 'variables',
          label: 'Переменные и скидка',
          children: <ClientVariablesTab key={variablesIdentity} userId={Number(id)} />,
        }] : []),
        ...(canReadStates ? [{
          key: 'states',
          label: 'Состояния',
          children: <ClientStatesTab userId={Number(id)} />,
        }] : []),
        {
          key: 'tags',
          label: <><TagsOutlined /> Метки</>,
          children: <ClientTagsBlock userId={Number(id)} />,
        },
        {
          key: 'orders',
          label: <><ShoppingCartOutlined /> Заказы ({stats.orders_count})</>,
          children: (
            <Table
              rowKey="id"
              columns={orderColumns}
              dataSource={orders}
              size="small"
              pagination={{ pageSize: 10, showTotal: (t: number) => `Всего: ${t}` }}
              expandable={{
                expandedRowRender: (record: OrderData) => (
                  <div style={{ padding: '8px 0' }}>
                    {record.items.map((item: OrderItemData, idx: number) => (
                      <div key={idx} style={{ marginBottom: 4 }}>
                        <Text strong>{item.product_name}</Text>
                        {item.variant_name && <Text type="secondary"> ({item.variant_name})</Text>}
                        <Text>{' \u00d7 '}{item.quantity} = {item.total} ₽</Text>
                        {item.addons.length > 0 && (
                          <div style={{ marginLeft: 16, color: '#888' }}>
                            + {item.addons.map((a: { name: string; price: number; qty: number }) => `${a.name} ${a.price}₽`).join(', ')}
                          </div>
                        )}
                      </div>
                    ))}
                    {record.delivery_address && (
                      <div style={{ marginTop: 8 }}>
                        <Text type="secondary">Адрес: {record.delivery_address}</Text>
                      </div>
                    )}
                    {record.comment && (
                      <div><Text type="secondary">Комментарий: {record.comment}</Text></div>
                    )}
                  </div>
                ),
              }}
            />
          ),
        },
        {
          key: 'bonus',
          label: <><GiftOutlined /> Бонусы ({user.bonus_balance})</>,
          children: (
            <div>
              <Space style={{ marginBottom: 16 }}>
                <Button
                  type="primary"
                  icon={<PlusCircleOutlined />}
                  onClick={() => { form.setFieldsValue({ amount: 100, comment: '' }); setAdjustModal(true); }}
                >
                  Начислить бонусы
                </Button>
                <Button
                  danger
                  icon={<MinusCircleOutlined />}
                  onClick={() => { form.setFieldsValue({ amount: -100, comment: '' }); setAdjustModal(true); }}
                >
                  Списать бонусы
                </Button>
              </Space>

              <Card size="small" title="Разбивка баланса" style={{ marginBottom: 16 }}>
                {bonus_breakdown.length === 0 ? (
                  <Empty description="Нет бонусных операций" />
                ) : (
                  <Row gutter={[16, 8]}>
                    {bonus_breakdown.map((b: BonusBreakdown) => (
                      <Col key={b.reason} xs={12} sm={8} md={6}>
                        <Card size="small" style={{ textAlign: 'center' }}>
                          <Tag color={REASON_COLORS[b.reason] || 'default'} style={{ marginBottom: 4 }}>
                            {REASON_LABELS[b.reason] || b.reason}
                          </Tag>
                          <div>
                            {b.earned > 0 && <Text type="success" strong>+{b.earned}</Text>}
                            {b.earned > 0 && b.spent > 0 && ' / '}
                            {b.spent > 0 && <Text type="danger" strong>-{b.spent}</Text>}
                          </div>
                          <Text type="secondary" style={{ fontSize: 12 }}>операций: {b.count}</Text>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                )}
              </Card>

              <Card size="small" title="История операций">
                <Table
                  rowKey="id"
                  columns={bonusColumns}
                  dataSource={bonus_transactions}
                  size="small"
                  pagination={{ pageSize: 15, showTotal: (t: number) => `Всего: ${t}` }}
                />
              </Card>
            </div>
          ),
        },
        {
          key: 'promos',
          label: <>Промокоды ({promo_codes.length})</>,
          children: promo_codes.length === 0 ? (
            <Empty description="Промокоды не использовались" />
          ) : (
            <Table
              rowKey="code"
              size="small"
              dataSource={promo_codes}
              columns={[
                { title: 'Промокод', dataIndex: 'code', render: (v: string) => <Tag color="blue">{v}</Tag> },
                { title: 'Использован раз', dataIndex: 'times_used' },
                { title: 'Общая скидка', dataIndex: 'total_discount', render: (v: number) => `${v} ₽` },
              ]}
            />
          ),
        },
      ]} />

      <Modal
        title="Корректировка бонусов"
        open={adjustModal}
        onCancel={() => setAdjustModal(false)}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleBonusAdjust}>
          <Form.Item
            name="amount"
            label="Сумма (положительная — начисление, отрицательная — списание)"
            rules={[{ required: true, message: 'Укажите сумму' }]}
          >
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="comment" label="Комментарий">
            <Input.TextArea rows={2} placeholder="Причина корректировки" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={adjustLoading}>
                Применить
              </Button>
              <Button onClick={() => setAdjustModal(false)}>Отмена</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
      {/* Модалка редактирования клиента */}
      <Modal
        title="Редактировать клиента"
        open={editModal}
        onCancel={() => setEditModal(false)}
        footer={null}
      >
        <Form form={editForm} layout="vertical" onFinish={handleEditClient}>
          <Form.Item name="name" label="Имя">
            <Input placeholder="Имя клиента" />
          </Form.Item>
          <Form.Item
            name="phone"
            label="Телефон"
            rules={[{ pattern: /^\+7\d{10}$/, message: 'Формат: +7XXXXXXXXXX' }]}
          >
            <Input placeholder="+79991234567" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={editLoading}>
                Сохранить
              </Button>
              <Button onClick={() => setEditModal(false)}>Отмена</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ClientProfilePage;
